/**
 * use-tenant-features Hook
 *
 * Provides access to tenant settings and UI mode.
 * Feature access (which products are available) is now derived from licenses,
 * not from tenant feature flags. Use useHasLicense() for product checks.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
  TenantSettings,
  TenantUIMode,
  DEFAULT_TENANT_SETTINGS,
} from '../types/tenant.types';
import { API_V1 } from '../API/APIs/APIv1';

/**
 * Fetch tenant settings from the API
 * Uses API_V1 axios client which includes automatic token refresh on 401
 */
async function fetchTenantSettings(): Promise<TenantSettings> {
  const response = await API_V1.get('/tenants/current/settings');
  return response.data;
}

/**
 * Update tenant settings via the API
 * Uses API_V1 axios client which includes automatic token refresh on 401
 */
async function updateTenantSettings(
  updates: Partial<TenantSettings>
): Promise<TenantSettings> {
  const response = await API_V1.patch('/tenants/current/settings', updates);
  return response.data;
}

/**
 * Query key for tenant settings
 */
export const TENANT_SETTINGS_QUERY_KEY = ['tenant', 'settings'];

/**
 * Hook to access and manage tenant settings
 */
export function useTenantFeatures() {
  const queryClient = useQueryClient();

  // Fetch tenant settings
  const {
    data: settings,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: TENANT_SETTINGS_QUERY_KEY,
    queryFn: fetchTenantSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Mutation to update settings
  const updateMutation = useMutation({
    mutationFn: updateTenantSettings,
    onSuccess: (newSettings) => {
      queryClient.setQueryData(TENANT_SETTINGS_QUERY_KEY, newSettings);
    },
  });

  const { mutateAsync } = updateMutation;

  // Use defaults while loading
  const currentSettings = settings ?? DEFAULT_TENANT_SETTINGS;
  const uiMode = currentSettings.uiMode ?? DEFAULT_TENANT_SETTINGS.uiMode;

  /**
   * Check if in simplified mode (AI Chat first experience)
   */
  const isSimplifiedMode = useMemo(() => uiMode === 'simplified', [uiMode]);

  /**
   * Check if in full mode (traditional feature overview)
   */
  const isFullMode = useMemo(() => uiMode === 'full', [uiMode]);

  /**
   * Update the UI mode
   */
  const setUIMode = useCallback(
    (mode: TenantUIMode) => {
      return mutateAsync({ uiMode: mode });
    },
    [mutateAsync]
  );

  /**
   * Check if Expert Campaign Builder toggle should be available
   */
  const showExpertCampaignBuilder = useMemo(
    () => currentSettings.expertCampaignBuilder ?? true,
    [currentSettings.expertCampaignBuilder]
  );

  /**
   * Get the landing page route based on UI mode
   */
  const getLandingPage = useCallback((): string => {
    return '/ai-chat';
  }, []);

  return {
    // Raw data
    settings: currentSettings,
    uiMode,

    // Loading states
    isLoading,
    error,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,

    // Feature checks (UI preferences, NOT license-gated features)
    showExpertCampaignBuilder,

    // Mode checks
    isSimplifiedMode,
    isFullMode,
    getLandingPage,

    // Mutations
    setUIMode,
    refetch,
  };
}

/**
 * Simple hook to just get the UI mode
 */
export function useUIMode(): {
  uiMode: TenantUIMode;
  isSimplifiedMode: boolean;
  isFullMode: boolean;
  isLoading: boolean;
} {
  const { uiMode, isSimplifiedMode, isFullMode, isLoading } = useTenantFeatures();
  return { uiMode, isSimplifiedMode, isFullMode, isLoading };
}

export default useTenantFeatures;
